import { useBasketRecipeUUIDs, useCoreIdToUUIDMap } from '../hooks'
import { useCallback } from 'react'
import { Collection, Recipe, RecipeCoreId, RecipeUUID } from '../types'
import { useSelector } from 'react-redux'

const compareCoreRecipeIds = (a: Recipe, b: Recipe) =>
  parseInt(a.coreId, 10) - parseInt(b.coreId, 10)

const renderRecipeName = (recipe: Recipe) =>
  [
    recipe.name,
    recipe.dietaryClaims.some((claim) => claim.slug === 'vegetarian' || claim.slug === 'vegan')
      ? '(V)'
      : undefined,
  ]
    .filter((part) => Boolean(part))
    .join(' ')

export const useAlternativeOptions = (
  collection: Collection | null,
  currentSelectedRecipeId: RecipeCoreId,
) => {
  const recipes = useSelector(({ recipesV3 }: any) => recipesV3.recipes) as Record<
    RecipeUUID,
    Recipe
  >

  const coreIdToUUID = useCoreIdToUUIDMap()
  const convertCoreIdToUUID = useCallback(
    (coreId: RecipeCoreId) => coreIdToUUID[coreId] || null,
    [coreIdToUUID],
  )

  const basketRecipeUUIDs = useBasketRecipeUUIDs()

  const collectionRecipeIdsIncludingOptions = new Set(
    (collection?.recipes || []).flatMap((recipe) => [
      recipe.id,
      ...recipe.options.map((option) => option.id),
    ]),
  )
  const offeredRecipes = Object.fromEntries(
    Object.keys(recipes)
      .map((id) => id as RecipeUUID)
      .filter((id) => collectionRecipeIdsIncludingOptions.has(id))
      .map((id) => [id, recipes[id]]),
  )

  if (!collection) {
    return []
  }

  const currentSelectedRecipeUUID = convertCoreIdToUUID(currentSelectedRecipeId)
  if (currentSelectedRecipeUUID === null) {
    return []
  }

  // recipeId passed into this function can be either the main recipe in a family or an option
  const collectionRecipeSet = collection.recipes.find(
    (recipe) =>
      recipe.id === currentSelectedRecipeUUID ||
      recipe.options.some((option) => option.id === currentSelectedRecipeUUID),
  )
  if (!collectionRecipeSet) {
    console.error('Currently selected recipe is not in the collection')

    return []
  }

  const allOptionIds = [
    collectionRecipeSet.id, // base recipe in the recipe set
    ...collectionRecipeSet.options.map((option) => option.id), // options in the recipe set
  ].filter((optionId) => optionId !== currentSelectedRecipeUUID)
  allOptionIds.unshift(currentSelectedRecipeUUID) // first one should be the selected recipe

  const options = allOptionIds
    .map((optionId) => offeredRecipes[optionId] || null)
    .filter((option): option is Recipe => option !== null)
    .sort(compareCoreRecipeIds)

  return options.map((recipe) => ({
    recipe,
    recipeId: recipe.coreId,
    recipeName: renderRecipeName(recipe),
    isChecked: String(currentSelectedRecipeId) === String(recipe.coreId),
    isOutOfStock: !offeredRecipes[recipe.id]?.isAvailable && !basketRecipeUUIDs.has(recipe.id),
    surcharge: recipe.surcharge ? recipe.surcharge.pricePerPortion / 100 : null,
  }))
}
